
// src/pages/Attendance/AttendanceDashboardPage.js
// Admin / Marketing Manager: everyone's attendance, Full Day / Half Day only.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  fetchAllAttendance,
  fetchAttendanceSummary,
} from '../../services/features/attendance/attendanceSlice';
import RangeBar from '../../components/Report/RangeBar';
import {
  computeRange,
  RANGE_PRESETS,
  STATUS_META,
  statusMeta,
  formatDayLabel,
  formatISTTime,
  formatDuration,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'PRESENT', label: 'Full Day' },
  { key: 'HALF_DAY', label: 'Half Day' },
  { key: 'ABSENT', label: 'Absent' },
  { key: 'ON_LEAVE', label: 'Leave' },
  { key: 'WEEK_OFF', label: 'Week Off' },
  { key: 'HOLIDAY', label: 'Holiday' },
];

const KPI_KEYS = ['PRESENT', 'HALF_DAY', 'ABSENT', 'ON_LEAVE', 'WEEK_OFF'];
const PAGE_SIZE = 25;

const AttendanceDashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, summary, loading } = useSelector((s) => s.attendance);

  // Default = last 7 days, so data shows immediately without typing a date.
  const [range, setRange] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { from, to } = useMemo(
    () => computeRange(range, customFrom, customTo),
    [range, customFrom, customTo]
  );
  const multiDay = from !== to;

  const load = useCallback(() => {
    dispatch(
      fetchAllAttendance({
        from,
        to,
        status: status || undefined,
        search: debounced || undefined,
        limit: 300,
      })
    );
    dispatch(fetchAttendanceSummary({ from, to }));
  }, [dispatch, from, to, status, debounced]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [range, status, debounced, customFrom, customTo]);

  const counts = summary?.summary || {};
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Insert a date heading whenever the day changes (multi-day ranges)
  const rows = useMemo(() => {
    const out = [];
    let last = null;
    pageItems.forEach((it) => {
      if (multiDay && it.date !== last) {
        out.push({ type: 'head', key: `h-${it.date}`, date: it.date });
        last = it.date;
      }
      out.push({ type: 'row', key: it._id, it });
    });
    return out;
  }, [pageItems, multiDay]);

  const openEmployee = (it) => {
    const emp = it.employeeId || {};
    if (!emp._id) return;
    const q = new URLSearchParams({
      name: emp.name || '',
      role: emp.role || '',
      mobile: emp.mobile || '',
      month: it.date.slice(0, 7),
      date: it.date,
    });
    navigate(`/attendance/${emp._id}?${q.toString()}`);
  };

  return (
    <div className="rp-page">
      <RangeBar
        presets={RANGE_PRESETS}
        range={range}
        onRange={setRange}
        from={customFrom}
        to={customTo}
        onFrom={setCustomFrom}
        onTo={setCustomTo}
      />

      <div className="rp-kpis">
        {KPI_KEYS.map((k) => (
          <div key={k} className="rp-kpi" style={{ '--kpi': STATUS_META[k].color }}>
            <div className="rp-kpi-value">{counts[k] || 0}</div>
            <div className="rp-kpi-label">{STATUS_META[k].label}</div>
          </div>
        ))}
      </div>

      <div className="rp-card">
        <div className="rp-toolbar" style={{ padding: '14px 16px' }}>
          <div className="rp-chips">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                className={`rp-chip ${status === f.key ? 'active' : ''}`}
                onClick={() => setStatus(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="rp-spacer" />
          <div className="rp-search">
            <Search size={14} />
            <input
              className="rp-input"
              placeholder="Search by name, mobile…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading && list.length === 0 ? (
          <div className="rp-loading">Loading…</div>
        ) : list.length === 0 ? (
          <div className="rp-empty">
            <b>No attendance records for this period</b>
            {range !== 'week' && range !== 'month' && (
              <button className="rp-chip active" style={{ marginTop: 12 }} onClick={() => setRange('week')}>
                Show last 7 days
              </button>
            )}
          </div>
        ) : (
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Hours</th>
                  {!multiDay ? null : <th>Date</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  if (r.type === 'head') {
                    return (
                      <tr key={r.key} className="rp-grouprow">
                        <td colSpan={7}>{formatDayLabel(r.date)}</td>
                      </tr>
                    );
                  }
                  const it = r.it;
                  const emp = it.employeeId || {};
                  const m = statusMeta(it.status);
                  return (
                    <tr key={r.key} className="clickable" onClick={() => openEmployee(it)}>
                      <td>
                        <div className="rp-person">
                          <div className="rp-avatar">{(emp.name || '?').charAt(0).toUpperCase()}</div>
                          <div style={{ minWidth: 0 }}>
                            <div className="rp-strong">{emp.name || '—'}</div>
                            <div className="rp-sub">{[emp.role, emp.mobile].filter(Boolean).join(' · ')}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="rp-pill" style={{ background: m.bg, color: m.color }}>
                          {m.label.toUpperCase()}
                        </span>
                      </td>
                      <td>{it.checkInTime ? formatISTTime(it.checkInTime) : '—'}</td>
                      <td>{it.checkOutTime ? formatISTTime(it.checkOutTime) : '—'}</td>
                      <td>{it.workDurationMinutes ? formatDuration(it.workDurationMinutes) : '—'}</td>
                      {multiDay && <td className="rp-muted">{it.date}</td>}
                      <td className="rp-num"><button className="rp-link">Calendar →</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {list.length > PAGE_SIZE && (
          <div className="rp-pager">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, list.length)} of {list.length}
            </span>
            <div className="rp-pager-btns">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceDashboardPage;


//------------ 13.10.26 Backup ----------------
// // src/pages/Attendance/AttendanceDashboardPage.js
// // Admin / Marketing Manager: everyone's attendance, Full Day / Half Day only.
// import React, { useCallback, useEffect, useMemo, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { Search } from 'lucide-react';
// import {
//   fetchAllAttendance,
//   fetchAttendanceSummary,
// } from '../../services/features/attendance/attendanceSlice';
// import RangeBar from '../../components/Report/RangeBar';
// import {
//   computeRange,
//   RANGE_PRESETS,
//   STATUS_META,
//   statusMeta,
//   formatDayLabel,
//   formatISTTime,
//   formatDuration,
// } from '../../utils/attendanceFormat';
// import '../Shared/ReportPages.css';

// const STATUS_FILTERS = [
//   { key: '', label: 'All' },
//   { key: 'PRESENT', label: 'Full Day' },
//   { key: 'HALF_DAY', label: 'Half Day' },
//   { key: 'ABSENT', label: 'Absent' },
//   { key: 'ON_LEAVE', label: 'Leave' },
//   { key: 'WEEK_OFF', label: 'Week Off' },
//   { key: 'HOLIDAY', label: 'Holiday' },
// ];

// const KPI_KEYS = ['PRESENT', 'HALF_DAY', 'ABSENT', 'ON_LEAVE', 'WEEK_OFF'];
// const PAGE_SIZE = 25;

// const AttendanceDashboardPage = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { list, summary, loading } = useSelector((s) => s.attendance);

//   // Default = last 7 days, so data shows immediately without typing a date.
//   const [range, setRange] = useState('week');
//   const [customFrom, setCustomFrom] = useState('');
//   const [customTo, setCustomTo] = useState('');
//   const [status, setStatus] = useState('');
//   const [search, setSearch] = useState('');
//   const [debounced, setDebounced] = useState('');
//   const [page, setPage] = useState(1);

//   useEffect(() => {
//     const t = setTimeout(() => setDebounced(search), 300);
//     return () => clearTimeout(t);
//   }, [search]);

//   const { from, to } = useMemo(
//     () => computeRange(range, customFrom, customTo),
//     [range, customFrom, customTo]
//   );
//   const multiDay = from !== to;

//   const load = useCallback(() => {
//     dispatch(
//       fetchAllAttendance({
//         from,
//         to,
//         status: status || undefined,
//         search: debounced || undefined,
//         limit: 300,
//       })
//     );
//     dispatch(fetchAttendanceSummary({ from, to }));
//   }, [dispatch, from, to, status, debounced]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   useEffect(() => setPage(1), [range, status, debounced, customFrom, customTo]);

//   const counts = summary?.summary || {};
//   const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
//   const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

//   // Insert a date heading whenever the day changes (multi-day ranges)
//   const rows = useMemo(() => {
//     const out = [];
//     let last = null;
//     pageItems.forEach((it) => {
//       if (multiDay && it.date !== last) {
//         out.push({ type: 'head', key: `h-${it.date}`, date: it.date });
//         last = it.date;
//       }
//       out.push({ type: 'row', key: it._id, it });
//     });
//     return out;
//   }, [pageItems, multiDay]);

//   const openEmployee = (it) => {
//     const emp = it.employeeId || {};
//     if (!emp._id) return;
//     const q = new URLSearchParams({
//       name: emp.name || '',
//       role: emp.role || '',
//       mobile: emp.mobile || '',
//       month: it.date.slice(0, 7),
//       date: it.date,
//     });
//     navigate(`/attendance/${emp._id}?${q.toString()}`);
//   };

//   return (
//     <div className="rp-page">
//       <RangeBar
//         presets={RANGE_PRESETS}
//         range={range}
//         onRange={setRange}
//         from={customFrom}
//         to={customTo}
//         onFrom={setCustomFrom}
//         onTo={setCustomTo}
//       />

//       <div className="rp-kpis">
//         {KPI_KEYS.map((k) => (
//           <div key={k} className="rp-kpi" style={{ '--kpi': STATUS_META[k].color }}>
//             <div className="rp-kpi-value">{counts[k] || 0}</div>
//             <div className="rp-kpi-label">{STATUS_META[k].label}</div>
//           </div>
//         ))}
//       </div>

//       <div className="rp-card">
//         <div className="rp-toolbar" style={{ padding: '14px 16px' }}>
//           <div className="rp-chips">
//             {STATUS_FILTERS.map((f) => (
//               <button
//                 key={f.key || 'all'}
//                 className={`rp-chip ${status === f.key ? 'active' : ''}`}
//                 onClick={() => setStatus(f.key)}
//               >
//                 {f.label}
//               </button>
//             ))}
//           </div>
//           <div className="rp-spacer" />
//           <div className="rp-search">
//             <Search size={14} />
//             <input
//               className="rp-input"
//               placeholder="Search by name, mobile…"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>
//         </div>

//         {loading && list.length === 0 ? (
//           <div className="rp-loading">Loading…</div>
//         ) : list.length === 0 ? (
//           <div className="rp-empty">
//             <b>No attendance records for this period</b>
//             {range !== 'week' && range !== 'month' && (
//               <button className="rp-chip active" style={{ marginTop: 12 }} onClick={() => setRange('week')}>
//                 Show last 7 days
//               </button>
//             )}
//           </div>
//         ) : (
//           <div className="rp-table-wrap">
//             <table className="rp-table">
//               <thead>
//                 <tr>
//                   <th>Employee</th>
//                   <th>Status</th>
//                   <th>Check In</th>
//                   <th>Check Out</th>
//                   <th>Hours</th>
//                   {!multiDay ? null : <th>Date</th>}
//                   <th />
//                 </tr>
//               </thead>
//               <tbody>
//                 {rows.map((r) => {
//                   if (r.type === 'head') {
//                     return (
//                       <tr key={r.key} className="rp-grouprow">
//                         <td colSpan={7}>{formatDayLabel(r.date)}</td>
//                       </tr>
//                     );
//                   }
//                   const it = r.it;
//                   const emp = it.employeeId || {};
//                   const m = statusMeta(it.status);
//                   return (
//                     <tr key={r.key} className="clickable" onClick={() => openEmployee(it)}>
//                       <td>
//                         <div className="rp-person">
//                           <div className="rp-avatar">{(emp.name || '?').charAt(0).toUpperCase()}</div>
//                           <div style={{ minWidth: 0 }}>
//                             <div className="rp-strong">{emp.name || '—'}</div>
//                             <div className="rp-sub">{[emp.role, emp.mobile].filter(Boolean).join(' · ')}</div>
//                           </div>
//                         </div>
//                       </td>
//                       <td>
//                         <span className="rp-pill" style={{ background: m.bg, color: m.color }}>
//                           {m.label.toUpperCase()}
//                         </span>
//                       </td>
//                       <td>{it.checkInTime ? formatISTTime(it.checkInTime) : '—'}</td>
//                       <td>{it.checkOutTime ? formatISTTime(it.checkOutTime) : '—'}</td>
//                       <td>{it.workDurationMinutes ? formatDuration(it.workDurationMinutes) : '—'}</td>
//                       {multiDay && <td className="rp-muted">{it.date}</td>}
//                       <td className="rp-num"><button className="rp-link">Calendar →</button></td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {list.length > PAGE_SIZE && (
//           <div className="rp-pager">
//             <span>
//               {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, list.length)} of {list.length}
//             </span>
//             <div className="rp-pager-btns">
//               <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
//               <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default AttendanceDashboardPage;
